import _ from 'underscore';

import * as ImportUtil from 'util/ImportUtil';

import {ChatFileUpload, ChatSelectedFile} from './ChatLibraryTypes';

/**
 * Unified helper function for handling file selection and upload in chat components.
 * This function manages the complete lifecycle of file uploads:
 * 1. Immediately adds files to state with loading state
 * 2. Uploads files to S3
 * 3. Updates state with completed upload data
 * 4. Removes files from state on upload failure
 *
 * @param files - Array of File objects to upload
 * @param selectedFiles - Current array of selected files
 * @param setSelectedFiles - State setter function for selected files
 */
export function handleSelectFiles(
  files: Array<File>,
  setSelectedFiles: (
    files: Array<ChatSelectedFile> | ((prev: Array<ChatSelectedFile>) => Array<ChatSelectedFile>)
  ) => void
): void {
  // Add files immediately, marked as loading
  const newSelectedFiles = files.map((file) => ({
    key: _.uniqueId('file'),
    file,
    completedUpload: null,
  }));
  setSelectedFiles((prev) => [...prev, ...newSelectedFiles]);

  // Upload each file and update state
  for (const selectedFile of newSelectedFiles) {
    uploadFile(selectedFile, setSelectedFiles);
  }
}

export async function uploadFile(
  selectedFile: ChatSelectedFile,
  setSelectedFiles: (
    files: Array<ChatSelectedFile> | ((prev: Array<ChatSelectedFile>) => Array<ChatSelectedFile>)
  ) => void
): Promise<void> {
  try {
    const s3Location = await ImportUtil.upload(selectedFile.file);
    const upload: ChatFileUpload = {
      s3Location: s3Location!,
      filename: selectedFile.file.name,
      mediaType: selectedFile.file.type,
    };

    // Update the selectedFile with upload data, implicitly removing the loading state.
    setSelectedFiles((currentFiles: Array<ChatSelectedFile>) =>
      currentFiles.map((f: ChatSelectedFile) =>
        f.key === selectedFile.key ? {...f, completedUpload: upload} : f
      )
    );
  } catch (error) {
    // Remove the file if upload failed
    setSelectedFiles((currentFiles: Array<ChatSelectedFile>) =>
      currentFiles.filter((f: ChatSelectedFile) => f.key !== selectedFile.key)
    );
    throw error;
  }
}
